import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";

const POLL_MS = 4000;

function formatTime(seconds) {
  const value = Number(seconds || 0);
  const mins = Math.floor(value / 60);
  const secs = Math.floor(value % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function parseExplanation(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return { why_foul_or_no_foul: value };
  }
}

export default function GameBatchUpload({ ruleset = "high_school" }) {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [plays, setPlays] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savingSegments, setSavingSegments] = useState(false);
  const [resultFilter, setResultFilter] = useState("");
  const [reviewFilter, setReviewFilter] = useState("");
  const [detailPlay, setDetailPlay] = useState(null);
  const [error, setError] = useState("");

  const selectedExplanation = useMemo(
    () => parseExplanation(detailPlay?.explanation),
    [detailPlay]
  );

  const fetchGames = useCallback(async () => {
    const params = new URLSearchParams({ ruleset, limit: "25", offset: "0" });
    const res = await apiFetch(`/games?${params.toString()}`);
    const data = await res.json();
    setGames(Array.isArray(data.items) ? data.items : []);
  }, [ruleset]);

  const fetchGameDetail = useCallback(async () => {
    if (!selectedGameId) return;
    const gameRes = await apiFetch(`/games/${selectedGameId}`);
    const gameData = await gameRes.json();
    setSelectedGame(gameData);

    const params = new URLSearchParams();
    if (resultFilter) params.set("result", resultFilter);
    if (reviewFilter) params.set("reviewed", reviewFilter);
    const playsRes = await apiFetch(
      `/games/${selectedGameId}/plays?${params.toString()}`
    );
    const playsData = await playsRes.json();
    setPlays(Array.isArray(playsData.items) ? playsData.items : []);
  }, [selectedGameId, resultFilter, reviewFilter]);

  useEffect(() => {
    fetchGames().catch((e) => setError(String(e)));
  }, [fetchGames]);

  useEffect(() => {
    fetchGameDetail().catch((e) => setError(String(e)));
    const interval = setInterval(() => {
      fetchGames().catch(() => {});
      fetchGameDetail().catch(() => {});
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchGames, fetchGameDetail]);

  async function uploadGame(e) {
    e.preventDefault();
    if (!file) {
      alert("Please select a full-game video first.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", title);
      formData.append("ruleset", ruleset);
      const res = await apiFetch(`/games/upload`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Game upload failed.");
      setTitle("");
      setFile(null);
      setSelectedGameId(data.id);
      await fetchGames();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  function updatePlay(index, patch) {
    setPlays((current) =>
      current.map((play, i) => (i === index ? { ...play, ...patch } : play))
    );
  }

  function addSegment() {
    const last = plays[plays.length - 1];
    const start = Number(last?.end_time || 0);
    setPlays((current) => [
      ...current,
      {
        id: `new-${Date.now()}`,
        play_number: current.length + 1,
        start_time: start,
        end_time: start + 20,
        notes: "",
        status: "segmented",
      },
    ]);
  }

  function removeSegment(index) {
    setPlays((current) => current.filter((_, i) => i !== index));
  }

  async function saveSegments() {
    if (!selectedGameId) return;
    setSavingSegments(true);
    setError("");
    try {
      const payload = {
        segments: plays.map((play, index) => ({
          play_number: Number(play.play_number || index + 1),
          start_time: Number(play.start_time || 0),
          end_time: Number(play.end_time || 0),
          notes: play.notes || "",
        })),
      };
      const res = await apiFetch(`/games/${selectedGameId}/segments`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not save segments.");
      await fetchGameDetail();
    } catch (e) {
      setError(String(e));
    } finally {
      setSavingSegments(false);
    }
  }

  async function analyzeGame() {
    if (!selectedGameId) return;
    setBusy(true);
    setError("");
    try {
      const res = await apiFetch(`/games/${selectedGameId}/analyze`, {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not start analysis.");
      await fetchGameDetail();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitReview(play, label) {
    const res = await apiFetch(`/review/${play.id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        human_label: label,
        human_notes: "",
        override_prediction: false,
      }),
    });
    const data = await res.json();
    if (!data.ok) alert(`Error: ${data.error || "failed"}`);
    await fetchGameDetail();
  }

  const canEditSegments =
    selectedGame && !["analyzing", "done"].includes(selectedGame.status);
  const canAnalyze =
    selectedGame &&
    plays.length > 0 &&
    ["segments_ready", "queued_for_analysis", "error"].includes(selectedGame.status);

  return (
    <section className="batchPanel">
      <div className="batchHeader">
        <div>
          <h2>Full Game Batch Evaluation</h2>
          <p>Upload a full game, approve candidate play segments, then run foul review for each play.</p>
        </div>
      </div>

      {error && <div className="errorBanner">{error}</div>}

      <form className="batchUploadForm" onSubmit={uploadGame}>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Game title"
        />
        <input
          type="file"
          accept="video/*"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
        <button type="submit" disabled={busy}>Upload Game</button>
      </form>

      <div className="batchLayout">
        <aside className="gameList">
          <div className="sectionTitle">Games</div>
          {games.map((game) => (
            <button
              key={game.id}
              className={game.id === selectedGameId ? "gameButton active" : "gameButton"}
              onClick={() => setSelectedGameId(game.id)}
              type="button"
            >
              <span>{game.title || `Game ${game.id}`}</span>
              <small>{game.status} · {Math.round((game.progress || 0) * 100)}%</small>
            </button>
          ))}
          {!games.length && <div className="emptyText">No games uploaded yet.</div>}
        </aside>

        <div className="gameWorkspace">
          {selectedGame ? (
            <>
              <div className="gameStatus">
                <div>
                  <strong>{selectedGame.title || `Game ${selectedGame.id}`}</strong>
                  <span>{selectedGame.status}</span>
                </div>
                <progress value={selectedGame.progress || 0} max="1" />
                <button onClick={analyzeGame} disabled={!canAnalyze || busy}>
                  Analyze Plays
                </button>
              </div>

              <div className="segmentToolbar">
                <div className="sectionTitle">Segments</div>
                <div className="segmentActions">
                  <button type="button" onClick={addSegment} disabled={!canEditSegments}>Add Segment</button>
                  <button type="button" onClick={saveSegments} disabled={!canEditSegments || savingSegments}>
                    Save Segments
                  </button>
                </div>
              </div>

              <div className="tableScroller">
                <table className="batchTable">
                  <thead>
                    <tr>
                      <th>Play</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Status</th>
                      <th>Result</th>
                      <th>Label</th>
                      <th>Conf.</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plays.map((play, index) => (
                      <tr key={play.id || index}>
                        <td>
                          <input
                            type="number"
                            value={play.play_number || index + 1}
                            onChange={(e) => updatePlay(index, { play_number: e.target.value })}
                            disabled={!canEditSegments}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.1"
                            value={play.start_time ?? 0}
                            onChange={(e) => updatePlay(index, { start_time: e.target.value })}
                            disabled={!canEditSegments}
                          />
                          <small>{formatTime(play.start_time)}</small>
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.1"
                            value={play.end_time ?? 0}
                            onChange={(e) => updatePlay(index, { end_time: e.target.value })}
                            disabled={!canEditSegments}
                          />
                          <small>{formatTime(play.end_time)}</small>
                        </td>
                        <td>{play.status}</td>
                        <td>{play.result || "—"}</td>
                        <td>{play.prediction_label || "—"}</td>
                        <td>{play.confidence != null ? Number(play.confidence).toFixed(2) : "—"}</td>
                        <td>
                          <input
                            value={play.notes || ""}
                            onChange={(e) => updatePlay(index, { notes: e.target.value })}
                            disabled={!canEditSegments}
                            placeholder="Optional"
                          />
                        </td>
                        <td>
                          <div className="rowActions">
                            <button type="button" onClick={() => setDetailPlay(play)}>Open</button>
                            <button type="button" onClick={() => removeSegment(index)} disabled={!canEditSegments}>
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!plays.length && (
                      <tr><td colSpan={9} className="emptyText">Segments will appear after upload processing.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="resultFilters">
                <select value={resultFilter} onChange={(e) => setResultFilter(e.target.value)}>
                  <option value="">All results</option>
                  <option value="FOUL">Fouls only</option>
                  <option value="NO FOUL">No foul</option>
                  <option value="UNCERTAIN">Uncertain</option>
                </select>
                <select value={reviewFilter} onChange={(e) => setReviewFilter(e.target.value)}>
                  <option value="">All reviews</option>
                  <option value="false">Needs review</option>
                  <option value="true">Reviewed</option>
                </select>
              </div>
            </>
          ) : (
            <div className="emptyWorkspace">Select or upload a game to begin.</div>
          )}
        </div>
      </div>

      {detailPlay && (
        <div className="batchDrawerBackdrop" onClick={() => setDetailPlay(null)}>
          <div className="batchDrawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawerTitle">
              <h3>Play {detailPlay.play_number}</h3>
              <button onClick={() => setDetailPlay(null)}>Close</button>
            </div>
            <div className="timeRange">
              {formatTime(detailPlay.start_time)} to {formatTime(detailPlay.end_time)}
            </div>
            {detailPlay.presigned_url ? (
              <video controls src={detailPlay.presigned_url} />
            ) : (
              <div className="emptyText">Clip is not available yet.</div>
            )}
            <div className="detailGrid">
              <div><span>Result</span><strong>{detailPlay.result || "—"}</strong></div>
              <div><span>Label</span><strong>{detailPlay.prediction_label || "—"}</strong></div>
              <div><span>Confidence</span><strong>{detailPlay.confidence != null ? Number(detailPlay.confidence).toFixed(2) : "—"}</strong></div>
            </div>
            {selectedExplanation && (
              <div className="reasoningBlock">
                <h4>Reasoning</h4>
                <p>{selectedExplanation.why_foul_or_no_foul || detailPlay.explanation}</p>
                {selectedExplanation.evidence_summary && <p><strong>Evidence:</strong> {selectedExplanation.evidence_summary}</p>}
                {selectedExplanation.rule_citations?.length ? (
                  <p><strong>Rules:</strong> {selectedExplanation.rule_citations.join(", ")}</p>
                ) : null}
                {selectedExplanation.uncertainty_notes && (
                  <p><strong>Uncertainty:</strong> {selectedExplanation.uncertainty_notes}</p>
                )}
              </div>
            )}
            <div className="drawerActions">
              <button onClick={() => submitReview(detailPlay, detailPlay.prediction_label || "Uncertain")}>
                Agree
              </button>
              <button onClick={() => submitReview(detailPlay, "Disagree")}>
                Mark Disagree
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
